/**
 * Challenges API — Supabase queries for challenge CRUD, membership, habits.
 * All functions use .throwOnError() per project rules.
 */

import { supabase } from '@turnifly/shared/api'
import {
  ChallengeProfileResponseSchema,
  LeaderboardEntrySchema,
  ReviewItemSchema,
  CHALLENGE_UPDATE_FIELDS,
} from '../types/challenge-profile-types'
import type {
  ChallengeProfileResponse,
  LeaderboardEntry,
  ReviewItem,
} from '../types/challenge-profile-types'
import { z } from 'zod/v4'

// ── TYPES ───────────────────────────────────────────────────

export interface CreateChallengeParams {
  creatorId: string
  type: 'private' | 'public' | 'competition'
  timeModel: 'fixed' | 'evergreen'
  name: string
  description?: string
  categoryId?: string
  coverImageUrl?: string
  coverEmoji?: string
  coverGradient?: string
  linkTitle?: string
  linkUrl?: string
  tags?: string[]
  startDate?: string
  durationDays: number
  genderFilter?: 'all' | 'female' | 'male'
  maxParticipants?: number | null
  creatorParticipates?: boolean
  joinPolicy?: 'before_start' | 'first_3_days' | 'anytime'
  isPaid?: boolean
  price?: number | null
  joinConditions?: Record<string, boolean>
  isHidden?: boolean
}

export interface CreateChallengeHabitParams {
  challengeId: string
  userId: string
  name: string
  icon?: string
  category?: string
  frequency?: string
  weeklyDays?: number[]
  customInterval?: number
  timeMode?: string
  timeDuration?: number
  timeStart?: string
  valueRequired?: boolean
  valueTarget?: number
  valueUnit?: string
  proofTypes?: string[]
  sortOrder?: number
}

export interface UserChallengeItem {
  id: string
  challenge_id: string
  challenge: {
    id: string
    name: string
    cover_image_url: string | null
    cover_emoji: string | null
    cover_gradient: string | null
    type: string
    status: string
    start_date: string | null
    end_date: string | null
    duration_days: number
    members_count: number
    category_id: string | null
    short_code: string | null
    creator_id: string
    avg_rating: number | null
    is_paid: boolean
    price: number | null
  }
  status: string
  attempt_number: number
  completion_rate: number | null
  joined_at: string
}

// ── SHORT CODE ──────────────────────────────────────────────

function generateShortCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  let code = ''
  const arr = new Uint8Array(8)
  crypto.getRandomValues(arr)
  for (let i = 0; i < 8; i++) {
    code += chars[arr[i] % chars.length]
  }
  return code
}

// ── CREATE CHALLENGE ────────────────────────────────────────

export async function createChallenge(params: CreateChallengeParams): Promise<unknown> {
  const endDate = params.startDate && params.durationDays
    ? (() => {
        const d = new Date(params.startDate)
        d.setDate(d.getDate() + params.durationDays - 1)
        return d.toISOString().split('T')[0]
      })()
    : null

  const { data } = await supabase
    .from('challenges')
    .insert({
      creator_id: params.creatorId,
      type: params.type,
      time_model: params.timeModel,
      name: params.name,
      description: params.description ?? null,
      category_id: params.categoryId ?? null,
      cover_image_url: params.coverImageUrl ?? null,
      cover_emoji: params.coverEmoji ?? null,
      cover_gradient: params.coverGradient ?? null,
      link_title: params.linkTitle ?? null,
      link_url: params.linkUrl ?? null,
      tags: params.tags ?? [],
      start_date: params.startDate ?? null,
      end_date: endDate,
      duration_days: params.durationDays,
      gender_filter: params.genderFilter ?? 'all',
      max_participants: params.maxParticipants ?? null,
      creator_participates: params.creatorParticipates ?? true,
      join_policy: params.type === 'private'
        ? 'before_start'
        : (params.joinPolicy ?? 'before_start'),
      is_paid: params.isPaid ?? false,
      price: params.price ?? null,
      commission_rate: 0.25,
      join_conditions: params.joinConditions ?? {},
      is_hidden: params.type === 'private' ? (params.isHidden ?? false) : false,
      status: 'upcoming',
      short_code: generateShortCode(),
    })
    .select()
    .single()
    .throwOnError()
  return data
}

// ── UPDATE CHALLENGE ────────────────────────────────────────

export async function updateChallenge(
  challengeId: string,
  updates: Record<string, unknown>,
): Promise<unknown> {
  const safeUpdates: Record<string, unknown> = { updated_at: new Date().toISOString() }
  const allowed = new Set<string>(CHALLENGE_UPDATE_FIELDS)

  for (const key of Object.keys(updates)) {
    if (allowed.has(key)) {
      safeUpdates[key] = updates[key]
    }
  }

  // Recalculate end_date if dates changed
  if (safeUpdates.start_date && safeUpdates.duration_days) {
    const d = new Date(safeUpdates.start_date as string)
    d.setDate(d.getDate() + (safeUpdates.duration_days as number) - 1)
    safeUpdates.end_date = d.toISOString().split('T')[0]
  }

  const { data } = await supabase
    .from('challenges')
    .update(safeUpdates)
    .eq('id', challengeId)
    .select()
    .single()
    .throwOnError()
  return data
}

// ── FETCH USER'S CHALLENGES (for profile tab) ───────────────

export async function fetchUserChallenges(userId: string): Promise<UserChallengeItem[]> {
  const { data: memberships } = await supabase
    .from('challenge_members')
    .select('id, challenge_id, status, attempt_number, completion_rate, joined_at')
    .eq('user_id', userId)
    .order('joined_at', { ascending: false })
    .throwOnError()

  if (!memberships || memberships.length === 0) return []

  const challengeIds = [
    ...new Set(memberships.map((m) => (m as { challenge_id: string }).challenge_id)),
  ]

  const { data: challenges } = await supabase
    .from('challenges')
    .select(
      'id, name, cover_image_url, cover_emoji, cover_gradient, type, status, start_date, end_date, duration_days, members_count, category_id, short_code, creator_id, avg_rating, is_paid, price',
    )
    .in('id', challengeIds)
    .throwOnError()

  const challengeMap = new Map<string, unknown>()
  for (const ch of challenges ?? []) {
    challengeMap.set((ch as { id: string }).id, ch)
  }

  return memberships
    .map((m) => {
      const mem = m as Record<string, unknown>
      return {
        ...mem,
        challenge: challengeMap.get(mem.challenge_id as string) ?? null,
      } as UserChallengeItem
    })
    .filter((m) => m.challenge !== null)
}

// ── FETCH CHALLENGE PROFILE (single RPC) ─────────────────────

export async function fetchChallengeProfile(
  challengeId: string,
): Promise<ChallengeProfileResponse> {
  const { data } = await supabase
    .rpc('get_challenge_profile', { p_challenge_id: challengeId })
    .throwOnError()

  return ChallengeProfileResponseSchema.parse(data)
}

// ── JOIN CHALLENGE ──────────────────────────────────────────

export async function joinChallenge(
  challengeId: string,
  userId: string,
): Promise<unknown> {
  const { data: existing } = await supabase
    .from('challenge_members')
    .select('attempt_number')
    .eq('challenge_id', challengeId)
    .eq('user_id', userId)
    .order('attempt_number', { ascending: false })
    .limit(1)
    .throwOnError()

  const nextAttempt =
    existing && existing.length > 0
      ? (existing[0] as { attempt_number: number }).attempt_number + 1
      : 1

  const { data } = await supabase
    .from('challenge_members')
    .insert({
      challenge_id: challengeId,
      user_id: userId,
      attempt_number: nextAttempt,
      status: 'active',
      joined_at: new Date().toISOString(),
    })
    .select()
    .single()
    .throwOnError()
  return data
}

// ── AUTO-JOIN CREATOR ───────────────────────────────────────

export async function autoJoinCreator(
  challengeId: string,
  userId: string,
): Promise<void> {
  await supabase
    .from('challenge_members')
    .insert({
      challenge_id: challengeId,
      user_id: userId,
      attempt_number: 1,
      status: 'active',
      joined_at: new Date().toISOString(),
    })
    .throwOnError()
}

// ── LEAVE CHALLENGE ─────────────────────────────────────────

export async function leaveChallenge(
  challengeId: string,
  userId: string,
): Promise<void> {
  await supabase
    .from('challenge_members')
    .update({ status: 'left' })
    .eq('challenge_id', challengeId)
    .eq('user_id', userId)
    .eq('status', 'active')
    .throwOnError()
}

// ── CREATE CHALLENGE HABIT ──────────────────────────────────

export async function createChallengeHabit(
  params: CreateChallengeHabitParams,
): Promise<unknown> {
  const { data } = await supabase
    .from('habits')
    .insert({
      user_id: params.userId,
      challenge_id: params.challengeId,
      name: params.name,
      icon: params.icon ?? '📌',
      category: params.category ?? null,
      frequency: params.frequency ?? 'daily',
      weekly_days: params.weeklyDays ?? null,
      custom_interval: params.customInterval ?? null,
      time_mode: params.timeMode ?? 'anytime',
      time_duration: params.timeDuration ?? null,
      time_start: params.timeStart ?? null,
      value_required: params.valueRequired ?? false,
      value_target: params.valueTarget ?? null,
      value_unit: params.valueUnit ?? null,
      proof_types: params.proofTypes ?? [],
      sort_order: params.sortOrder ?? 0,
      is_active: true,
    })
    .select()
    .single()
    .throwOnError()
  return data
}

// ── DELETE CHALLENGE HABIT ──────────────────────────────────

export async function deleteChallengeHabit(habitId: string): Promise<void> {
  await supabase
    .from('habits')
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq('id', habitId)
    .throwOnError()
}

// ── COPY CHALLENGE HABITS TO USER ───────────────────────────
// When user joins a challenge, copy challenge habits to their personal list

export async function copyChallengeHabitsToUser(
  challengeId: string,
  userId: string,
  startDate: string | null,
  endDate: string | null,
): Promise<void> {
  // Fetch challenge's template habits (created by challenge creator)
  const { data: templateHabits } = await supabase
    .from('habits')
    .select(
      'name, icon, category, frequency, weekly_days, custom_interval, time_mode, time_start, time_duration, value_required, value_target, value_unit, proof_types, sort_order',
    )
    .eq('challenge_id', challengeId)
    .eq('is_active', true)
    .order('sort_order')
    .throwOnError()

  if (!templateHabits || templateHabits.length === 0) return

  const userHabits = templateHabits.map((h, idx) => {
    const habit = h as Record<string, unknown>
    return {
      user_id: userId,
      challenge_id: challengeId,
      name: habit.name,
      icon: habit.icon ?? '📌',
      category: habit.category ?? null,
      frequency: habit.frequency ?? 'daily',
      weekly_days: habit.weekly_days ?? null,
      custom_interval: habit.custom_interval ?? null,
      time_mode: habit.time_mode ?? 'anytime',
      time_start: habit.time_start ?? null,
      time_duration: habit.time_duration ?? null,
      value_required: habit.value_required ?? false,
      value_target: habit.value_target ?? null,
      value_unit: habit.value_unit ?? null,
      proof_types: habit.proof_types ?? [],
      sort_order: habit.sort_order ?? idx,
      is_active: true,
      start_date: startDate,
      end_date: endDate,
    }
  })

  await supabase.from('habits').insert(userHabits).throwOnError()
}

// ── DEACTIVATE USER'S CHALLENGE HABITS ──────────────────────

export async function deactivateChallengeHabits(
  challengeId: string,
  userId: string,
): Promise<void> {
  await supabase
    .from('habits')
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq('challenge_id', challengeId)
    .eq('user_id', userId)
    .throwOnError()
}

// ── FETCH LEADERBOARD PAGE (paginated) ───────────────────────

export async function fetchLeaderboardPage(
  challengeId: string,
  offset: number,
  limit: number,
): Promise<LeaderboardEntry[]> {
  const { data } = await supabase
    .rpc('get_challenge_leaderboard', {
      p_challenge_id: challengeId,
      p_limit: limit,
      p_offset: offset,
    })
    .throwOnError()

  return z.array(LeaderboardEntrySchema).parse(data ?? [])
}

// ── SUBMIT REVIEW ────────────────────────────────────────────

export async function submitChallengeReview(
  challengeId: string,
  rating: number,
  text: string | null,
): Promise<void> {
  await supabase
    .rpc('submit_challenge_review', {
      p_challenge_id: challengeId,
      p_rating: rating,
      p_text: text,
    })
    .throwOnError()
}

// ── FETCH REVIEWS (paginated) ────────────────────────────────

export async function fetchChallengeReviews(
  challengeId: string,
  limit: number,
  offset: number,
): Promise<ReviewItem[]> {
  const { data } = await supabase
    .from('challenge_reviews')
    .select(
      'id, rating, text, created_at, user_id, profiles!inner(full_name, avatar_url, badge_type)',
    )
    .eq('challenge_id', challengeId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)
    .throwOnError()

  const rows = (data ?? []).map((row: Record<string, unknown>) => {
    const profile = row.profiles as Record<string, unknown> | null
    return {
      id: row.id,
      rating: row.rating,
      text: row.text,
      created_at: row.created_at,
      user_id: row.user_id,
      full_name: profile?.full_name ?? '',
      avatar_url: profile?.avatar_url ?? null,
      badge_type: profile?.badge_type ?? null,
    }
  })

  return z.array(ReviewItemSchema).parse(rows)
}

// ── CHALLENGE STATS (for profile) ───────────────────────────

export interface ChallengeStats {
  participating: number
  completed: number
  created: number
  avgCompletionRate: number
}

export async function fetchChallengeStats(userId: string): Promise<ChallengeStats> {
  const [participatingRes, completedRes, createdRes, avgRes] = await Promise.all([
    supabase
      .from('challenge_members')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('status', 'active')
      .throwOnError(),
    supabase
      .from('challenge_members')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('status', 'completed')
      .throwOnError(),
    supabase
      .from('challenges')
      .select('id', { count: 'exact', head: true })
      .eq('creator_id', userId)
      .throwOnError(),
    supabase
      .from('challenge_members')
      .select('completion_rate')
      .eq('user_id', userId)
      .eq('status', 'completed')
      .throwOnError(),
  ])

  const rates = ((avgRes.data ?? []) as Array<{ completion_rate: number | null }>)
    .map((r) => r.completion_rate ?? 0)
  const avg = rates.length > 0
    ? Math.round(rates.reduce((s, v) => s + v, 0) / rates.length)
    : 0

  return {
    participating: participatingRes.count ?? 0,
    completed: completedRes.count ?? 0,
    created: createdRes.count ?? 0,
    avgCompletionRate: avg,
  }
}

// ── FETCH COMPLETED CHALLENGES (for profile achievements section) ──

export interface CompletedChallengeItem {
  id: string
  name: string
  cover_emoji: string | null
  cover_gradient: string | null
  type: string
  duration_days: number
  completion_rate: number
  completed_at: string
  is_hidden: boolean
}

export async function fetchCompletedChallenges(
  userId: string,
): Promise<CompletedChallengeItem[]> {
  const { data: members } = await supabase
    .from('challenge_members')
    .select('challenge_id, completion_rate, completed_at')
    .eq('user_id', userId)
    .eq('status', 'completed')
    .not('completed_at', 'is', null)
    .order('completed_at', { ascending: false })
    .throwOnError()

  if (!members || members.length === 0) return []

  const ids = [...new Set((members as Array<{ challenge_id: string }>).map((m) => m.challenge_id))]

  const { data: challenges } = await supabase
    .from('challenges')
    .select('id, name, cover_emoji, cover_gradient, type, duration_days, is_hidden')
    .in('id', ids)
    .throwOnError()

  const chMap = new Map<string, Record<string, unknown>>()
  for (const ch of (challenges ?? []) as Array<Record<string, unknown>>) {
    chMap.set(ch.id as string, ch)
  }

  return (members as Array<Record<string, unknown>>)
    .map((m) => {
      const ch = chMap.get(m.challenge_id as string)
      if (!ch) return null
      return {
        id: ch.id as string,
        name: ch.name as string,
        cover_emoji: ch.cover_emoji as string | null,
        cover_gradient: ch.cover_gradient as string | null,
        type: ch.type as string,
        duration_days: ch.duration_days as number,
        completion_rate: (m.completion_rate as number) ?? 0,
        completed_at: m.completed_at as string,
        is_hidden: ch.is_hidden as boolean,
      }
    })
    .filter((x): x is CompletedChallengeItem => x !== null)
}

// ── SHARE LINK ──────────────────────────────────────────────

export function getChallengeShareLink(shortCode: string): string {
  return `https://t.me/TurniflyBot?start=challenge_${shortCode}`
}

// ── BROWSE CHALLENGES ──────────────────────────────────────

export interface BrowseChallengeItem {
  id: string
  name: string
  description: string | null
  cover_image_url: string | null
  cover_emoji: string | null
  cover_gradient: string | null
  type: string
  time_model: string
  status: string
  start_date: string | null
  end_date: string | null
  duration_days: number
  members_count: number
  avg_rating: number | null
  reviews_count: number
  category_id: string | null
  is_paid: boolean
  price: number | null
  short_code: string | null
  creator_id: string
  tags: string[]
  created_at: string
}

const BROWSE_SELECT = 'id, name, description, cover_image_url, cover_emoji, cover_gradient, type, time_model, status, start_date, end_date, duration_days, members_count, avg_rating, reviews_count, category_id, is_paid, price, short_code, creator_id, tags, created_at'

export async function fetchBrowseChallenges(params: {
  category?: string | null
  types?: string[]
  statuses?: string[]
  sort?: 'popular' | 'newest' | 'rating'
  limit?: number
  offset?: number
}): Promise<BrowseChallengeItem[]> {
  let query = supabase
    .from('challenges')
    .select(BROWSE_SELECT)
    .eq('is_hidden', false)

  if (params.types && params.types.length > 0) {
    query = query.in('type', params.types)
  }

  if (params.statuses && params.statuses.length > 0) {
    query = query.in('status', params.statuses)
  } else {
    query = query.in('status', ['upcoming', 'active'])
  }

  if (params.category) {
    query = query.eq('category_id', params.category)
  }

  const sortField = params.sort === 'rating' ? 'avg_rating'
    : params.sort === 'newest' ? 'created_at'
    : 'members_count'

  query = query
    .order(sortField, { ascending: false, nullsFirst: false })
    .range(params.offset ?? 0, (params.offset ?? 0) + (params.limit ?? 20) - 1)

  const { data } = await query.throwOnError()
  return (data ?? []) as BrowseChallengeItem[]
}

// ── FETCH CATEGORIES ───────────────────────────────────────

export interface CategoryItem {
  id: string
  name_uz: string
  name_en: string
  icon: string
  sort_order: number
}

export async function fetchChallengeCategories(): Promise<CategoryItem[]> {
  const { data } = await supabase
    .from('categories')
    .select('id, name_uz, name_en, icon, sort_order')
    .order('sort_order')
    .throwOnError()
  return (data ?? []) as CategoryItem[]
}

// ── FETCH AD BANNERS ───────────────────────────────────────

export interface AdBannerItem {
  id: string
  title: string
  subtitle: string | null
  image_url: string
  link_type: string
  link_target: string
  priority: number
}

export async function fetchAdBanners(): Promise<AdBannerItem[]> {
  const now = new Date().toISOString()
  const { data } = await supabase
    .from('ad_banners')
    .select('id, title, subtitle, image_url, link_type, link_target, priority')
    .eq('is_active', true)
    .or(`starts_at.is.null,starts_at.lte.${now}`)
    .or(`ends_at.is.null,ends_at.gte.${now}`)
    .order('priority', { ascending: false })
    .throwOnError()
  return (data ?? []) as AdBannerItem[]
}

// ── FETCH COMPETITIONS ─────────────────────────────────────

export async function fetchCompetitions(params: {
  status: 'active' | 'upcoming' | 'completed'
  limit?: number
  offset?: number
}): Promise<BrowseChallengeItem[]> {
  const { data } = await supabase
    .from('challenges')
    .select(BROWSE_SELECT)
    .eq('type', 'competition')
    .eq('is_hidden', false)
    .eq('status', params.status)
    .order(params.status === 'completed' ? 'end_date' : 'start_date', {
      ascending: params.status === 'upcoming',
      nullsFirst: false,
    })
    .range(params.offset ?? 0, (params.offset ?? 0) + (params.limit ?? 20) - 1)
    .throwOnError()
  return (data ?? []) as BrowseChallengeItem[]
}

// ── SEARCH CHALLENGES ──────────────────────────────────────

export async function searchChallenges(
  query: string,
  category?: string | null,
  limit?: number,
): Promise<BrowseChallengeItem[]> {
  let q = supabase
    .from('challenges')
    .select(BROWSE_SELECT)
    .eq('is_hidden', false)
    .in('status', ['upcoming', 'active'])
    .ilike('name', `%${query}%`)

  if (category) {
    q = q.eq('category_id', category)
  }

  q = q.order('members_count', { ascending: false }).limit(limit ?? 20)

  const { data } = await q.throwOnError()
  return (data ?? []) as BrowseChallengeItem[]
}
